import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import { getApiCompaniesCurrentServicemodulesTimetrackingServiceModuleIdTimetrackingCompany } from '@/api/generated/company-management-user/company-management-user'
import {
  getApiCompaniesCurrentServicemodulesTimetrackingServiceModuleIdTimequotaBalance,
  getApiCompaniesCurrentServicemodulesTimetrackingServiceModuleIdTimequotaCurrent,
} from '@/api/generated/company-user/company-user'
import { TimeTrackingApproval } from '@/api/model'
import type { EmployeeUserResponse, TimeQuotaBalanceModel, TimeQuotaModel } from '@/api/model'

/** Computed time-tracking metrics of an employee (in hours). */
export interface EmployeeTimeTrackingSummary {
  /** Hours already worked in the current quota. */
  workedHours: number | null
  /** Planned working hours in the current quota. */
  plannedHours: number | null
  /** Total balance (over-/undertime) in hours. */
  balanceHours: number | null
  /** Open approvals (entries with status `Pending`). */
  pendingApprovals: number
}

/** Loading state + result per employee, so the table can react per row. */
export interface TimeTrackingSummaryState {
  summary?: EmployeeTimeTrackingSummary
  isPending: boolean
  isError: boolean
}

/** Minutes to hours (2 decimal places), analogous to the old portal. */
function toHours(minutes: number): number {
  return Math.round((minutes / 60) * 100) / 100
}

/** Metrics of an employee from current quota + balance + open approvals. */
async function loadSummary(
  serviceModuleId: number,
  employeeId: string,
): Promise<EmployeeTimeTrackingSummary> {
  const [current, balance, pending] = await Promise.all([
    getApiCompaniesCurrentServicemodulesTimetrackingServiceModuleIdTimequotaCurrent(serviceModuleId, { employeeId })
      .then((r) => (r.data || undefined) as TimeQuotaModel | undefined)
      .catch(() => undefined),
    getApiCompaniesCurrentServicemodulesTimetrackingServiceModuleIdTimequotaBalance(serviceModuleId, { employeeId })
      .then((r) => (r.data || undefined) as TimeQuotaBalanceModel | undefined)
      .catch(() => undefined),
    getApiCompaniesCurrentServicemodulesTimetrackingServiceModuleIdTimetrackingCompany(serviceModuleId, {
      Offset: 0,
      Limit: 1,
      employeeUserId: employeeId,
      approval: TimeTrackingApproval.Pending,
    })
      .then((r) => r.data?.totalCount ?? 0)
      .catch(() => 0),
  ])

  return {
    workedHours: current?.workedMinutesBalance == null ? null : toHours(current.workedMinutesBalance),
    plannedHours: current?.plannedWorkMinutes == null ? null : toHours(current.plannedWorkMinutes),
    balanceHours: balance?.totalBalance == null ? null : toHours(balance.totalBalance),
    pendingApprovals: pending,
  }
}

/**
 * Loads the time-tracking metrics for a list of employees via fan-out — one
 * React Query per employee (current quota + balance + number of open approvals).
 * Returns a map (employee id → state), so the table can look up loading
 * state/value per row. Mirrors `useAbsenceSummaries`.
 */
export function useTimeTrackingSummaries(serviceModuleId: number, employees: EmployeeUserResponse[]) {
  const results = useQueries({
    queries: employees.map((employee) => ({
      queryKey: ['timetracking-summary', serviceModuleId, employee.id] as const,
      queryFn: () => loadSummary(serviceModuleId, employee.id ?? ''),
      enabled: !!employee.id,
      staleTime: 60_000,
    })),
  })

  const summariesById = useMemo(() => {
    const map = new Map<string, TimeTrackingSummaryState>()
    employees.forEach((employee, index) => {
      if (!employee.id) return
      const result = results[index]
      map.set(employee.id, {
        summary: result?.data,
        isPending: result?.isPending ?? false,
        isError: result?.isError ?? false,
      })
    })
    return map
  }, [employees, results])

  return { summariesById }
}
